import { useState } from "react";

import { Callout, Classes, Dialog, Intent } from "@blueprintjs/core";

import { Box, Flex } from "@/components/layout/flexbox";
import { DefaultButton } from "@/components/pieces/button/DefaultButton";
import { PrimaryButton } from "@/components/pieces/button/PrimaryButton";
import { Avatar } from "@/components/pieces/users/Avatar";
import { getUser } from "@/components/session/JwtTokenManager";
import { INNER_BORDER_COLOR } from "@/css/constants";
import {
  GetCoachingProgramsDocument,
  MeetingStatus,
  ProgramComponentsQueryDocument,
  useSelectCoachForCoachingMutation,
} from "@/graphql";
import { MeetingCoach } from "@/types/coaches";
import { ParticipantMeeting } from "@/types/meetings";
import { ParticipantComponent } from "@/types/programs";
import { ensureNonNullArray } from "@/utils/array";
import { formatDate } from "@/utils/datetime";
import { NonNullableArrayItem } from "@/utils/types";

type ChildComponent = NonNullableArrayItem<
  NonNullable<ParticipantComponent["childComponents"]>
>;

interface CoachItemProps {
  coach: MeetingCoach;
  meeting: ParticipantMeeting;
  component: ChildComponent;
  upcomingMeetings: ParticipantMeeting[];
}

function CoachItem({
  coach,
  meeting,
  component,
  upcomingMeetings,
}: CoachItemProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const user = getUser();

  const [selectCoach, { loading: saving }] = useSelectCoachForCoachingMutation({
    refetchQueries: [
      {
        query: ProgramComponentsQueryDocument,
        variables: {
          programId: component?.programId,
          userId: user?.id,
        },
      },
      {
        query: GetCoachingProgramsDocument,
        variables: {
          coachId: coach.id,
        },
      },
    ],
    onCompleted: () => {
      setIsOpen(false);
      setErrorMessage(null);
    },
    onError: (error) => {
      setErrorMessage(error.message);
    },
  });

  return (
    <Flex
      className="xs:rounded-md xs:gap-3.5 xs:p-4 items-center justify-between bg-white md:gap-4 md:rounded-lg md:p-6"
      style={{
        background: "white",
        border: `1px solid ${INNER_BORDER_COLOR}`,
      }}
    >
      <div className="xs:gap-3.5 flex items-center md:gap-4">
        <Avatar user={coach} size={50} />
        <div className="xs:gap-0.5 flex flex-col md:gap-1">
          <div className="text-base font-semibold text-gray-800">
            {coach.name}
          </div>
          <div className="text-base font-normal text-gray-500">
            {meeting?.eventAt
              ? `Last session: ${formatDate(meeting.eventAt, "MMM d, yyyy")}`
              : "No meeting scheduled"}
          </div>
        </div>
      </div>
      <PrimaryButton
        className="px-6 py-2.5 text-base"
        onClick={() => {
          setIsOpen(!isOpen);
        }}
      >
        Choose
      </PrimaryButton>
      <WarningCancelRemainingIntroSessionDialog
        upcomingMeetings={upcomingMeetings}
        isOpen={isOpen}
        onCancel={() => setIsOpen(false)}
        onConfirm={() => {
          selectCoach({
            variables: { componentId: component?.id, coachId: coach?.id },
          });
        }}
        loading={saving}
        errorMessage={errorMessage}
      />
    </Flex>
  );
}

type CoachType = ParticipantComponent["selectedCoach"];

interface CoachSelectionAfterIntroProps {
  component: ChildComponent;
  selectedCoach: CoachType;
}

export function CoachSelectionAfterIntro({
  component,
  selectedCoach,
}: CoachSelectionAfterIntroProps) {
  const meetings = ensureNonNullArray(component?.meetings);

  const completedIntroMeetings = meetings.filter(
    (meeting) =>
      meeting.status === MeetingStatus.Complete &&
      meeting?.product?.introProduct === true,
  );

  const upcomingMeetings = meetings.filter(
    (meeting) =>
      meeting.status &&
      [
        MeetingStatus.Scheduling,
        MeetingStatus.Upcoming,
        MeetingStatus.Soon,
      ].includes(meeting.status),
  );
  const hasCompletedMeetings = Boolean(completedIntroMeetings?.length);

  if (!hasCompletedMeetings || (selectedCoach && hasCompletedMeetings)) {
    return null;
  }

  return (
    <div className="xs:p-4 xs:rounded-md xs:gap-4 flex flex-col bg-gradient-to-br from-[#F2FAFF] to-[#D8ECF8] md:gap-6 md:rounded-lg md:p-6">
      <div className="xs:gap-0.5 flex flex-col md:gap-1">
        <div className="text-lg font-semibold text-blue-800">
          Choose your coach and schedule
        </div>
        <div className="xs:text-sm mt-1 font-normal text-gray-700 md:text-base">
          After selecting your coach, you'll schedule your next session together
        </div>
      </div>
      <div className="xs:gap-2.5 flex flex-col md:gap-3">
        {completedIntroMeetings.map((meeting) => {
          const { coach } = meeting;
          if (!coach) return null;

          return (
            <CoachItem
              key={coach.id}
              coach={coach}
              meeting={meeting}
              component={component}
              upcomingMeetings={upcomingMeetings}
            />
          );
        })}
      </div>
    </div>
  );
}

interface WarningCancelRemainingIntroSessionDialogProps {
  upcomingMeetings: ParticipantMeeting[];
  isOpen: boolean;
  onCancel: () => void;
  onConfirm: () => void;
  loading: boolean;
  errorMessage: string | null;
}

export function WarningCancelRemainingIntroSessionDialog({
  upcomingMeetings,
  isOpen,
  onCancel,
  onConfirm,
  loading,
  errorMessage,
}: WarningCancelRemainingIntroSessionDialogProps) {
  const hasUpcoming = upcomingMeetings?.length > 0;
  const content = hasUpcoming ? (
    <>
      <Box fontWeight={600} mb={2}>
        The following sessions will be cancelled:
      </Box>
      {upcomingMeetings?.map((meeting) => {
        const { coach } = meeting;
        return (
          <Box
            key={meeting?.id}
            backgroundColor="white"
            p={2}
            style={{
              border: `1px solid ${INNER_BORDER_COLOR}`,
              borderRadius: "8px",
              marginBottom: "16px",
            }}
          >
            <Box fontWeight={600}>Intro Session</Box>
            <Box fontWeight={400}>
              {" "}
              {`With ${coach?.name} on ${formatDate(
                meeting.eventAt,
                "MMM d 'at' h:mm a",
              )}`}
            </Box>
          </Box>
        );
      })}
      <Box fontWeight={400} pb={2}>
        These cancellations will be subject to our standard cancellation policy.
        This action cannot be undone.
      </Box>
    </>
  ) : (
    <Box fontWeight={400} pb={2}>
      Selecting a coach will close your intro period. You won't be able to book
      additional intro sessions.
    </Box>
  );

  return (
    <Dialog
      isOpen={isOpen}
      onClose={onCancel}
      title={
        <Box fontSize="20px" fontWeight={600} pb={2}>
          Confirm coach selection
        </Box>
      }
    >
      <Box m={3} className={`${Classes.DIALOG_BODY} ${Classes.RUNNING_TEXT}`}>
        {errorMessage && (
          <Callout intent={Intent.DANGER}>{errorMessage}</Callout>
        )}
        {content}
        <Flex justifyContent="flex-end" mt={4}>
          <DefaultButton onClick={onCancel}>Cancel</DefaultButton>
          <DefaultButton
            intent="primary"
            onClick={onConfirm}
            style={{ marginLeft: "8px" }}
            disabled={loading}
            loading={loading}
          >
            Confirm Selection
          </DefaultButton>
        </Flex>
      </Box>
    </Dialog>
  );
}
